import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import moment from 'moment'
import 'moment/locale/es'
import 'moment/locale/fr'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import dfnsEs from 'date-fns/locale/es'
import dfnsFr from 'date-fns/locale/fr'

import enTranslations from 'i18n/locales/en.json'
import esTranslations from 'i18n/locales/es.json'
import frTranslations from 'i18n/locales/fr.json'
import { DEFAULT_LANGUAGE } from 'i18n/config'

// Register non-English locales with react-datepicker (used by
// @PipelineDeals/shared-react-components' DateInput). Without this, the
// date picker renders month names, weekday headers, and the year/month
// dropdowns in English regardless of i18next's current language.
registerLocale('es', dfnsEs)
registerLocale('fr', dfnsFr)

// Keep moment, react-datepicker, and i18next in lockstep. Fires on every
// language change (including the initial load below).
const syncDateLocales = (lng) => {
  const normalized = (lng || DEFAULT_LANGUAGE).split('-')[0]
  moment.locale(normalized)
  // react-datepicker falls back to English for any unregistered locale
  setDefaultLocale(normalized === DEFAULT_LANGUAGE ? undefined : normalized)
}

const resources = {
  en: {
    translation: enTranslations
  },
  es: {
    translation: esTranslations
  },
  fr: {
    translation: frTranslations
  }
}

const i18nConfig = {
  resources,
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false // React already escapes values
  },
  react: {
    useSuspense: false
  },
  // Surface missing keys in dev so dynamic/computed call sites the static
  // scanner can't see (e.g. `t(someVar)`, template-literal suffixes) get
  // caught the moment the UI renders. Production stays silent.
  saveMissing: process.env.NODE_ENV !== 'production',
  missingKeyHandler: (lngs, ns, key) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[i18n] Missing key: ${key} for ${lngs.join(',')}`)
    }
  }
}

// Initialize with English by default
i18nConfig.lng = DEFAULT_LANGUAGE

// Always add LanguageDetector (it's safe to add even if we don't use detection)
i18n.use(LanguageDetector)

// Initialize i18n with default config
i18n
  .use(initReactI18next)
  .init(i18nConfig)

// Initial sync (covers cold boot) + subscribe so subsequent changeLanguage
// calls also propagate to moment and react-datepicker.
syncDateLocales(i18n.language)
i18n.on('languageChanged', syncDateLocales)

// Function to update language - called from ConnectedI18nProvider
export const updateLanguage = (language, featureEnabled) => {
  if (!featureEnabled) {
    i18n.changeLanguage(DEFAULT_LANGUAGE)
    i18n.options.detection = undefined
    return
  }

  // Enable language detection if feature is enabled
  i18n.options.detection = {
    order: ['localStorage', 'navigator', 'htmlTag'],
    lookupLocalStorage: 'i18nextLng',
    caches: ['localStorage']
  }

  // Use user preference if available and valid, otherwise detect from localStorage/navigator
  if (language && resources[language]) {
    i18n.changeLanguage(language)
  } else {
    // Fall back to detection
    let detectedLng = null
    if (typeof window !== 'undefined' && window.localStorage) {
      detectedLng = window.localStorage.getItem('i18nextLng')
    }
    if (!detectedLng && typeof window !== 'undefined' && window.navigator) {
      const navLang = window.navigator.language || window.navigator.userLanguage
      if (navLang) {
        detectedLng = navLang.split('-')[0]
      }
    }
    if (detectedLng && resources[detectedLng]) {
      i18n.changeLanguage(detectedLng)
    } else {
      i18n.changeLanguage(DEFAULT_LANGUAGE)
    }
  }
}

export default i18n
